import React, { useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from "react-router"
import { Modal, Dropdown } from 'react-bootstrap'
import Select from 'react-select'
import DatePicker from "react-datepicker"

import FilterSearch from 'pages/parts/FilterSearch'
import FilterPagination from 'pages/parts/FilterPagination'
import DebtCorrectionModal from 'pages/parts/DebtCorrectionModal'

import { httpOk, GET, POST, FILE_D } from 'helpers/api'
import {
	formatDate, formatMoney, returnSign,
	findKeyFromArrayByValue, returnMinDate, regexPhoneNumber, PAGE_SIZES,
	buildCashAccountSelectOptions, getAccountSelectProps, pickCashAccountId
} from 'helpers/helpers'
import { renderSortIndicator, patchSortFilterOnColumnClick, renderSortDirectionIcon } from 'helpers/tableSortIndicator'

function Index() {
	const { t } = useTranslation()
	const reduxAccount = useSelector(state => state.account)
	const date = new Date()

	const [data, setData] = useState([])
	const [poses, setPoses] = useState([])
	const [accounts, setAccounts] = useState([])
	const [counterparties, setCounterparties] = useState([])
	const [paymentPurposeTypes, setPaymentPurposeTypes] = useState([])
	const [modalPayment, setModalPayment] = useState(false)
	const [correctionItem, setCorrectionItem] = useState(null)
	const [selectedItem, setSelectedItem] = useState({ 'created_at': date })
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [filterDataExist, setFilterDataExist] = useState(false)
	const [filterData, setFilterData] = useState({
		pos_id: reduxAccount.pos_id,
		order_by: null,
		sort: "asc",
		size: 20,
	})
	const [pagination, setPagination] = useState({
		url: `/report-suppliers`,
		size: 20,
		response: null
	})

	async function searchWithFilter(nextFilterData = filterData, passedData = {}) {
		var filterUrl = `/report-suppliers`
		var urlParams = ""
		const filterDataCopy = { ...nextFilterData }
		checkFilter(filterDataCopy)

		if (filterDataCopy.pos_id)
			urlParams += returnSign(urlParams) + 'pos_id=' + filterDataCopy.pos_id
		if (filterDataCopy.search)
			urlParams += returnSign(urlParams) + 'search=' + filterDataCopy.search
		if (filterDataCopy.page)
			urlParams += returnSign(urlParams) + 'page=' + filterDataCopy.page
		if (filterDataCopy.size)
			urlParams += returnSign(urlParams) + 'size=' + filterDataCopy.size
		if (filterDataCopy.order_by) {
			const ob = filterDataCopy.order_by === "phone" ? null : filterDataCopy.order_by
			if (ob) {
				urlParams += returnSign(urlParams) + "order_by=" + ob
				urlParams += returnSign(urlParams) + "sort=" + (filterDataCopy.sort === "desc" ? "desc" : "asc")
			}
		}

		if (passedData['excel']) {
			urlParams += returnSign(urlParams) + 'excel=true'

			const responseExcel = await FILE_D(filterUrl + urlParams)
			if (httpOk(responseExcel)) {
				const file = new Blob([responseExcel.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
				const fileUrl = URL.createObjectURL(file);
				const link = document.createElement('a');
				link.href = fileUrl;
				link.setAttribute('download', `suppliers.xlsx`);
				document.body.appendChild(link);
				link.click();
				URL.revokeObjectURL(fileUrl);
			}
			return
		}

		if (`/report-suppliers${urlParams}` !== filterUrl)
			setFilterDataExist(true)

		filterUrl = filterUrl + urlParams

		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			setPagination({ ...pagination, 'url': filterUrl, 'size': filterDataCopy.size, 'response': response.data })
		}

		setDropdownIsOpen(false)
	}

	async function createExpense(e) {
		e.preventDefault();

		var response = await POST('/expense-web', selectedItem, { loader: true })

		if (httpOk(response)) {
			toggleModalPayment()
			setSelectedItem({ 'created_at': date })
			searchWithFilter()
		}
	}

	async function handleSelectPPT(option) {
		setSelectedItem({
			...selectedItem,
			'payment_purpose_id': option.id,
			'account_chef_id': pickCashAccountId(accounts, option.account_chef_id),
			'account_core_id': option.account_core_id,
			'counterparty': option.counterparty ?? null,
		})

		if (option.counterparty) getCounterparty(option)
		if (!option.counterparty) setCounterparties([])
	}

	async function getCounterparty(option) {
		var response = await POST('/helper/counterparties-type', {
			'payment_purpose_id': option.id
		}, { loader: true })

		if (httpOk(response)) setCounterparties(response.data)
	}

	function toggleModalPayment(bool = false, item = null) {
		var itemCopy = { ...item }
		// Дату — как из календаря: полный Date уходит в JSON временем по UTC, и после
		// полуночи по Ташкенту (до 05:00) оплата ложилась на вчера, на 5 часов раньше
		itemCopy.created_at = formatDate(new Date(), 'yyyy-MM-dd')

		if (bool) {
			itemCopy.counterparty_id = item.supplier_id
			setSelectedItem(itemCopy)
		} else {
			itemCopy.amount = ''
			itemCopy.note = null
			itemCopy.counterparty_id = null
			setSelectedItem(itemCopy)
		}

		setModalPayment(bool)
	}

	function resetFilter() {
		const next = {
			pos_id: reduxAccount.pos_id,
			search: "",
			page: null,
			order_by: null,
			sort: "asc",
			size: 20,
		}
		setFilterData(next)
		searchWithFilter(next)
	}

	function handleSortColumn(columnKey) {
		const next = patchSortFilterOnColumnClick(filterData, columnKey)
		setFilterData(next)
		searchWithFilter(next)
	}

	function sortColumnLabel(key) {
		if (key === "name") return t("supplier")
		if (key === "balance") return t("debt")
		return key
	}

	function checkFilter(currentFilterData = filterData) {
		if (
			currentFilterData.pos_id === null &&
			currentFilterData.search === ''
		) {
			setFilterDataExist(false)
		}
	}

	function toggleDropdown() {
		setDropdownIsOpen(!dropdownIsOpen)
	}

	async function paginate(page) {
		const nextFilterData = { ...filterData, 'page': page }
		setFilterData(nextFilterData)
		searchWithFilter(nextFilterData)
	}

	function handlePageSizeChange(size) {
		const nextFilterData = { ...filterData, size, page: null }
		setFilterData(nextFilterData)
		searchWithFilter(nextFilterData)
	}

	async function getPoses() {
		const response = await GET('/helper/poses')
		if (httpOk(response)) setPoses(response.data)
	}

	async function getAccounts() {
		const response = await GET('/helper/accounts')
		if (httpOk(response)) setAccounts(response.data)
	}

	async function getPaymentPurposeTypes() {
		const response = await GET('/helper/ppt-expense?counterparty=supplier')
		if (httpOk(response)) setPaymentPurposeTypes(response.data)
	}

	useEffect(() => {
		getPoses()
		getAccounts()
		getPaymentPurposeTypes()
		searchWithFilter()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const accountChefOptions = buildCashAccountSelectOptions(accounts)

	return (
		<>
			<div className="page-header">
				<h4 className="vertical-center h-100">
					{t('suppliers')} [{data.total}]
				</h4>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex justify-content-between flex-wrap mb-3">
						<div className="d-flex gap-2">
							<FilterSearch
								filterData={filterData}
								setFilterData={setFilterData}
								onSearchChange={(search) => {
									const nextFilterData = { ...filterData, search, page: null }
									setFilterData(nextFilterData)
									searchWithFilter(nextFilterData)
								}}>
							</FilterSearch>
							<Dropdown onToggle={toggleDropdown} show={dropdownIsOpen}>
								<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
									{t('filter')}
								</Dropdown.Toggle>

								<Dropdown.Menu className="dropdown-filter">
									<div className="row">
										<div className="form-group col-md-6">
											<label>{t('pos')}</label>
											<Select
												options={poses}
												value={poses.find(option => option.id === filterData.pos_id) || ''}
												onChange={(option) => setFilterData({ ...filterData, 'pos_id': option.id })}
												placeholder=""
												noOptionsMessage={() => t('list_empty')}
												getOptionLabel={(option) => option.name}
												getOptionValue={(option) => option.id}
											/>
										</div>
									</div>
									<div className="row mt-3">
										<div className="col-md-12">
											<div className="d-flex justify-content-end gap-2">
												<button className="btn btn-secondary"
													onClick={() => setDropdownIsOpen(false)}>
													{t('cancel')}
												</button>
												<button className="btn btn-primary"
													onClick={() => searchWithFilter({ ...filterData, page: null })}>
													{t('apply')}
												</button>
											</div>
										</div>
									</div>
								</Dropdown.Menu>
							</Dropdown>
							<button className="btn btn-outline-primary btn-wide-2"
								onClick={() => searchWithFilter(filterData, { 'excel': true })}>
								<i className="uil uil-file-download-alt"></i>
							</button>
						</div>
						<div className="d-flex gap-2">
							<div className="vertical-center">
								<h4><b>{t('amount')}</b>:</h4>
							</div>
							<div className="vertical-center">
								<h5 className="text-end">
									<span className={`${data.total_amount >= 0 ? 'text-success' : 'text-danger'}`}>
										{formatMoney(data.total_amount)}
									</span>
								</h5>
							</div>
						</div>
					</div>

					{filterDataExist &&
						<div className="filter-block">
							<div className="filter">
								<strong>{t('filter')}</strong>
								<br />
								{filterData.pos_id &&
									<span className="me-2">
										<b>{t('pos')}: </b>
										{findKeyFromArrayByValue(poses, 'id', filterData.pos_id, 'name')}
									</span>
								}
								{filterData.search &&
									<span className="me-2">
										<b>{t('search')}: </b>
										<span>{filterData.search}</span>
									</span>
								}
								{filterData.order_by &&
									<span className="me-2 d-inline-flex align-items-center flex-wrap gap-1">
										<b>{t("sort_by")}: </b>
										<span className="d-inline-flex align-items-center gap-1">
											{sortColumnLabel(filterData.order_by)}
											{renderSortDirectionIcon(filterData.sort, {
												ariaLabel:
													filterData.sort === "desc"
														? t("sort_descending")
														: t("sort_ascending"),
												title:
													filterData.sort === "desc"
														? t("sort_descending")
														: t("sort_ascending"),
											})}
										</span>
									</span>
								}
							</div>
							<div className="vertical-center">
								<button className="btn btn-outline-dark text-uppercase" onClick={resetFilter}>
									{t('reset')}
								</button>
							</div>
						</div>
					}

					<div className="table-responsive">
						<table className="table table-bordered table-hover mb-0">
							<thead>
								<tr>
									<th style={{ width: "33px", minWidth: "33px", maxWidth: "33px" }} className="text-center">#</th>
									<th
										style={{ width: "20%" }}
										className="th-sortable user-select-none"
										onClick={() => handleSortColumn("name")}
										title={t("sort_by")}>
										<div className="d-flex align-items-center justify-content-between gap-2 pe-1">
											<span>{t("supplier")}</span>
											{renderSortIndicator("name", filterData)}
										</div>
									</th>
									<th style={{ width: "10%" }}>{t("phone")}</th>
									<th	style={{ width: "30%" }} className="td-ellipsis">
										{t("last_action")}
									</th>
									<th	style={{ width: "30%" }}
										className="th-sortable user-select-none"
										onClick={() => handleSortColumn("last_action")}
										title={t("sort_by")}>
										<div className="text-center">
											<span>{t("action_date")}</span>
											{renderSortIndicator("last_action", filterData)}
										</div>
									</th>
									<th
										style={{ width: "10%" }}
										className="th-sortable user-select-none"
										onClick={() => handleSortColumn("balance")}
										title={t("sort_by")}>
										<div className="d-flex align-items-center justify-content-end gap-2 ps-1">
											<span>{t("debt")}</span>
											{renderSortIndicator("balance", filterData)}
										</div>
									</th>
									<th style={{ width: "33px", minWidth: "33px", maxWidth: "33px" }} />
								</tr>
							</thead>
							<tbody>
								{data?.data?.map((item, index) => (
									<tr className="tr-middle" key={index}>
										<td style={{ width: "33px", minWidth: "33px", maxWidth: "33px" }} className="text-center">{item.row_number}</td>
										<td className="td-ellipsis">
											<Link className="text-primary cursor table-first-column-clickable"
												to={`/report-suppliers/debts/${item.supplier_id}`}>
												{item.name}
											</Link>
										</td>
										<td className="text-nowrap">{regexPhoneNumber(item.phone)}</td>
										<td className="text-nowrap">
											{item.last_action_at
												? t(item.last_action_type || "correction")
												: t("no_actions")}
										</td>
										<td className="text-nowrap text-center">
											{item.last_action_at ? formatDate(item.last_action_at) : "—"}
										</td>
										<td className="text-end text-nowrap">
											<div className={item.balance >= 0 ? 'text-primary' : 'text-danger'}>
												<span>{formatMoney(item.balance)}</span>
											</div>
										</td>
										<td style={{ width: "33px", minWidth: "33px", maxWidth: "33px" }}>
											<div className="table-dropdown">
												<Dropdown id={`dropdown-${index}`}>
													<Dropdown.Toggle variant="outline-primary">
														<i className="uil uil-ellipsis-v"></i>
													</Dropdown.Toggle>

													<Dropdown.Menu>
														<div className="table-dropdown-item"
															onClick={() => toggleModalPayment(true, item)}>
															<i className="uil uil-transaction"></i>
															{t('payment')}
														</div>
														<div className="table-dropdown-item"
															onClick={() => setCorrectionItem(item)}>
															<i className="uil uil-balance-scale"></i>
															{t('debt_correction')}
														</div>
													</Dropdown.Menu>
												</Dropdown>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>

					{pagination.response &&
						<FilterPagination
							pagination={pagination}
							paginate={paginate}
							showPageSizeSelect
							pageSizeOptions={PAGE_SIZES}
							onPageSizeChange={handlePageSizeChange}>
						</FilterPagination>
					}
				</div>
			</div>

			{/* MODAL PAYMENT */}
			<Modal show={modalPayment} animation={false}
				centered dialogClassName="update-modal-width"
 onHide={() => toggleModalPayment()}>
				<Modal.Header closeButton>
					<Modal.Title>{t('expense')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<form onSubmit={createExpense} autoComplete="off">
						{reduxAccount.role === 'ROLE_OWNER' &&
							<div className="form-group">
								<label>{t('pos')}</label>
								<Select
									options={poses}
									value={poses.find(option => option.id === selectedItem.pos_id)}
									onChange={(option) => setSelectedItem({ ...selectedItem, 'pos_id': option.id })}
									placeholder=""
									noOptionsMessage={() => t('list_empty')}
									getOptionLabel={(option) => option.name}
									getOptionValue={(option) => option.id}
								/>
							</div>
						}
						<div className="form-group">
							<label>{t('date')}</label>
							<div className="calendar-input">
								<DatePicker
									showMonthDropdown
									showYearDropdown
									dropdownMode="select"
									yearDropdownItemNumber={2}
									className="form-control"
									dateFormat="dd.MM.yyyy"
									selected={selectedItem.created_at ? new Date(selectedItem.created_at) : ''}
									onChange={(date) => setSelectedItem({ ...selectedItem, 'created_at': formatDate(date, 'yyyy-MM-dd') })}
									minDate={returnMinDate(new Date(), 20)}
									maxDate={new Date()}
								/>
								<i className="uil uil-calendar-alt"></i>
							</div>
						</div>
						<div className="form-group">
							<label>{t('expense_type')}<span className="required-mark">*</span></label>
							<Select
								isDisabled={selectedItem.update}
								options={paymentPurposeTypes}
								value={paymentPurposeTypes.find(option => option.id === selectedItem.payment_purpose_id)}
								onChange={(option) => handleSelectPPT(option)}
								placeholder=""
								noOptionsMessage={() => t('list_empty')}
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
							/>
						</div>
						<div className="form-group">
							<label>{t('counterparty')}<span className="required-mark">*</span></label>
							<Select
								isDisabled
								options={counterparties}
								value={counterparties.find(option => option.id && option.id === selectedItem.counterparty_id) || null}
								onChange={(option) => setSelectedItem({ ...selectedItem, 'counterparty_id': option ? option.id : null })}
								placeholder=""
								noOptionsMessage={() => t('list_empty')}
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
							/>
						</div>
						<div className="form-group">
							<label>{t('operation_account')}<span className="required-mark">*</span></label>
							<Select
								isClearable
								{...getAccountSelectProps(t)}
								options={accountChefOptions}
								value={accountChefOptions.find(option => option.id === selectedItem.account_chef_id) || null}
								onChange={(option) => setSelectedItem({ ...selectedItem, 'account_chef_id': option ? option.id : null })}
							/>
						</div>
						<div className="form-group d-none">
							<label>{t('report_account')}</label>
							<Select
								options={accounts}
								value={accounts.find(option => option.id === selectedItem.account_core_id)}
								onChange={(option) => setSelectedItem({ ...selectedItem, 'account_core_id': option ? option.id : null })}
								placeholder=""
								noOptionsMessage={() => t('list_empty')}
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
							/>
						</div>
						<div className="form-group">
							<label>{t('amount')}<span className="required-mark">*</span></label>
							<input type="text" className="form-control" name="amount"
								value={selectedItem.amount ? formatMoney(selectedItem.amount) : ''}
								onChange={(e) => setSelectedItem({ ...selectedItem, 'amount': e.target.value.replace(/[^0-9.]/g, '') })} />
						</div>
						<div className="form-group">
							<label>{t('note')}</label>
							<input type="text" className="form-control" name="note"
								value={selectedItem.note ?? ''}
								onChange={(e) => setSelectedItem({ ...selectedItem, 'note': e.target.value })} />
						</div>
						<div className="d-flex w-100 mt-3 gap-2">
							<button type="button" className="btn btn-outline-warning w-100"
								onClick={() => toggleModalPayment()} tabIndex="-1">
								{t('cancel')}
							</button>
							{selectedItem.id ?
								<button type="submit" className="btn btn-primary w-100"
									disabled={!selectedItem.amount}>
									{t('save')}
								</button>
								:
								<button type="submit" className="btn btn-primary w-100">
									{t('save')}
								</button>
							}
						</div>
					</form>
				</Modal.Body>
			</Modal>

			<DebtCorrectionModal
				show={!!correctionItem}
				onHide={() => setCorrectionItem(null)}
				onSuccess={() => searchWithFilter()}
				counterparty="supplier"
				item={correctionItem || {}}
				posId={filterData.pos_id || reduxAccount.pos_id} />
		</>
	)
}

export default Index
